import { formatOdd } from '@common/components/odds/formatUtils';
import { APP_ONE_CLICK_BET } from '@common/constants';
import { logout } from '@features/auth/slice/authSlice';
import { basketApi } from '@features/basket/api/basketApi';
import { BasketBetType, BasketStatus, IBasket } from '@features/basket/api/basketApi.type';
import { IBasketBonusCard, IBasketState, IOneClickBet } from '@features/basket/slice/basketSlice.type';
import { hasRebetCountdown } from '@features/basket/slice/rebet';
import { IAddToBasket } from '@features/basket/api/basketApi.type';
import { applyEditResponse, IBetEditState, removeOdd, toggleOdd } from '@features/basket/edit/betEdit';
import { createSlice, Draft, PayloadAction } from '@reduxjs/toolkit';
import { reactLocalStorage } from 'reactjs-localstorage';

const NON_REFUSAL_STATUSES: (BasketStatus | undefined)[] = [
  BasketStatus.EMPTY,
  BasketStatus.REQUEST,
  BasketStatus.CONFIRMED,
  BasketStatus.LINE_CHANGE,
  BasketStatus.SUM_CHANGE,
  BasketStatus.CANCELLED,
];

const placementRefusal = (state: Draft<IBasketState>, payload: IBasket) => {
  if (state.oneClickBet.allow || !payload.message) return undefined;
  if (payload.status === BasketStatus.EVENT_REBET_BLOCK) {
    return hasRebetCountdown(payload.items) ? undefined : payload.message;
  }
  return NON_REFUSAL_STATUSES.includes(payload.status) ? undefined : payload.message;
};

const basketCaseReducer = (state: Draft<IBasketState>, payload: IBasket) => {
  if (payload.limits) {
    state.limits = payload.limits;
  }
  switch (payload.status) {
    case BasketStatus.EMPTY:
      state.ids = [];
      state.items = [];
      state.betType = BasketBetType.SINGLE;
      state.expressAvailable = false;
      state.expressSum = undefined;
      state.status = undefined;
      state.message = undefined;
      state.messageId = undefined;
      break;
    case BasketStatus.CONFIRMED:
      state.ids = [];
      state.items = [];
      state.betType = BasketBetType.SINGLE;
      state.expressAvailable = false;
      state.expressSum = undefined;
      state.status = payload.status;
      state.message = payload.message;
      state.messageId = payload.message ? Date.now() : undefined;
      break;
    case BasketStatus.REQUEST:
      state.status = payload.status;
      break;
    case BasketStatus.CANCELLED:
      state.ids = [];
      state.items = [];
      state.betType = BasketBetType.SINGLE;
      state.expressAvailable = false;
      state.status = payload.status;
      state.message = payload.message;
      state.messageId = payload.message ? Date.now() : undefined;
      break;
    case BasketStatus.LINE_CHANGE:
      state.ids = payload.items.map((item) => item.id);
      state.items = payload.betType === BasketBetType.EXPRESS ? payload.items.map((item) => {
        const index = state.items.findIndex((currentItem) => currentItem.id === item.id);
        if (index !== -1) {
          return {
            ...item,
            sum: state.items[index].sum,
          };
        }
        return item;
      }) : payload.items;
      state.betType = payload.betType;
      state.expressAvailable = payload.express_available;
      state.expressSum = (payload.betType === BasketBetType.EXPRESS && payload.items[0]?.sum)
        ? parseInt(payload.items[0].sum.toString())
        : state.expressSum;
      state.status = payload.status;
      state.message = payload.message;
      state.messageId = payload.message ? Date.now() : undefined;
      break;
    case BasketStatus.REQUEST_BAN:
    case BasketStatus.EMPTY_PLACE_BET:
    case BasketStatus.USER_NOT_VERIFIED:
    case BasketStatus.BONUS_CARD_LOW_RATE:
    case BasketStatus.BET_NOT_ALLOWED:
    case BasketStatus.BONUS_MIN_ITEMS:
      state.status = payload.status;
      state.message = payload.message;
      state.messageId = payload.message ? Date.now() : undefined;
      break;
    case BasketStatus.SUM_CHANGE:
      if (payload.betType === BasketBetType.SINGLE) {
        state.items = payload.items;
      } else if (payload.betType === BasketBetType.EXPRESS) {
        state.expressSum = parseInt(payload.items[0].sum.toString());
      }
      state.status = payload.status;
      state.message = payload.message;
      state.messageId = payload.message ? Date.now() : undefined;
      break;
    case BasketStatus.VALIDATION_BASKET_ADD:
      state.status = payload.status;
      state.message = payload.message;
      state.messageId = payload.message ? Date.now() : undefined;
      break;
    case BasketStatus.UNPROCESSED:
    case BasketStatus.INSUFFICIENT_FUNDS:
    case BasketStatus.EVENT_REBET_BLOCK:
    default:
      const items = payload.items ?? [];
      state.ids = items.map((item) => item.id);
      state.items = items;
      state.betType = payload.betType ?? state.betType;
      state.expressAvailable = payload.express_available ?? state.expressAvailable;
      if (payload.maxExpress) {
        state.maxExpress = payload.maxExpress;
      }
      state.status = payload.status;
      state.message = payload.message;
      state.messageId = payload.message ? Date.now() : undefined;
  }
};
const oneClickBet = reactLocalStorage.getObject(APP_ONE_CLICK_BET, {
  allow: false,
  betAmount: 10,
}) as IOneClickBet;
const initialState: IBasketState = {
  ids: [],
  items: [],
  betType: BasketBetType.SINGLE,
  oneClickBet: oneClickBet,
  expressAvailable: false,
};
const basketSlice = createSlice({
  name: 'basket',
  initialState: initialState,
  reducers: {
    setBetType: (state, action: PayloadAction<BasketBetType>) => {
      delete state.refusal;
      state.betType = action.payload;
      delete state.bonusCard;
    },
    setExpressSum: (state, action: PayloadAction<number | ''>) => {
      if (state.items.length) {
        state.expressSum = action.payload || undefined;
      }
    },
    setBasketItemSum: (state, action: PayloadAction<{ itemId: string, sum: number | string }>) => {
      if (state.items.length) {
        const index = state.items.findIndex((item) => item.id === action.payload.itemId);
        if (index !== -1) {
          state.items[index].sum = action.payload.sum;
        }
      }
    },
    setOneClickBet: (state, action: PayloadAction<IOneClickBet>) => {
      state.oneClickBet = action.payload;
    },
    resetBasketStatus: (state) => {
      delete state.status;
      delete state.message;
    },
    clearBasketRefusal: (state) => {
      delete state.refusal;
    },
    setBasketBonusCard: (state, action: PayloadAction<IBasketBonusCard>) => {
      delete state.refusal;
      if (state.bonusCard && state.bonusCard.no === action.payload.no) {
        delete state.bonusCard;
      } else {
        state.bonusCard = action.payload;
      }
    },
    changeBasketItemValue: (state, action: PayloadAction<{
      eventId: number,
      odds: Record<string, string>,
      interest: number
    }>) => {
      state.items.forEach((item) => {
        if (item.event.id !== action.payload.eventId) {
          return;
        }
        const currentValue = formatOdd(item.value);
        const newValue = formatOdd(action.payload.odds[`${item.outcomeSportId}_${item.outcomeItemId}`], action.payload.interest);
        if (currentValue !== newValue) {
          item.value = newValue;
          item.oldValue = currentValue;
        }
      });
    },
    lockBasketItem: (state, action: PayloadAction<{ eventId: number, lock: boolean }>) => {
      state.items.forEach((item) => {
        if (item.event.id === action.payload.eventId) {
          item.event.lock = action.payload.lock;
        }
      });
    },
    lockBasketLiveItems: (state, action: PayloadAction<boolean>) => {
      if (state.items.length) {
        state.items.forEach((item) => {
          if (item.event.isLive) {
            item.event.lock = action.payload;
          }
        });
      }
    },
    startBetEdit: (state, action: PayloadAction<Omit<IBetEditState, 'odds' | 'loading'>>) => {
      state.edit = { ...action.payload, odds: [], loading: false };
    },
    toggleBetEditOdd: (state, action: PayloadAction<IAddToBasket>) => {
      if (state.edit) toggleOdd(state.edit, action.payload);
    },
    removeBetEditOdd: (state, action: PayloadAction<string>) => {
      if (state.edit) removeOdd(state.edit, action.payload);
    },
    resetBetEditMessage: (state) => {
      if (!state.edit) return;
      delete state.edit.status;
      delete state.edit.message;
    },
    cancelBetEdit: (state) => {
      delete state.edit;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(logout, (state) => {
        delete state.edit;
        state.ids = [];
        state.items = [];
        state.betType = BasketBetType.SINGLE;
        state.expressAvailable = false;
        state.expressSum = undefined;
        state.status = undefined;
        state.message = undefined;
        delete state.refusal;
      })
      .addMatcher(basketApi.endpoints.fetchBasketItems.matchFulfilled, (state, { payload }) => {
        const placing = state.status === BasketStatus.REQUEST;
        basketCaseReducer(state, payload);
        if (placing) {
          state.refusal = placementRefusal(state, payload);
        }
      })
      .addMatcher(basketApi.endpoints.addOddToBasket.matchFulfilled, (state, { payload }) => {
        basketCaseReducer(state, payload);
        delete state.refusal;
      })
      .addMatcher(basketApi.endpoints.deleteItem.matchFulfilled, (state, { payload }) => {
        basketCaseReducer(state, payload);
        delete state.refusal;
      })
      .addMatcher(basketApi.endpoints.clearBasket.matchFulfilled, (state, { payload }) => {
        basketCaseReducer(state, payload);
        delete state.refusal;
      })
      .addMatcher(basketApi.endpoints.placeBet.matchPending, (state) => {
        delete state.refusal;
      })
      .addMatcher(basketApi.endpoints.placeBet.matchFulfilled, (state, { payload }) => {
        basketCaseReducer(state, payload);
        state.refusal = placementRefusal(state, payload);
      })
      .addMatcher(basketApi.endpoints.editTicket.matchPending, (state, { meta }) => {
        if (state.edit && state.edit.ticketNo === meta.arg.originalArgs.ticketNo) {
          state.edit.loading = true;
        }
      })
      .addMatcher(basketApi.endpoints.editTicket.matchRejected, (state, { meta }) => {
        if (!state.edit || state.edit.ticketNo !== meta.arg.originalArgs.ticketNo) return;
        state.edit.loading = false;
        if (meta.arg.originalArgs.preview) return;
        state.edit.status = 'EDIT_TICKET_FAILED';
        state.edit.message = undefined;
        state.edit.messageId = Date.now();
      })
      .addMatcher(basketApi.endpoints.editTicket.matchFulfilled, (state, { payload, meta }) => {
        if (state.edit && state.edit.ticketNo === meta.arg.originalArgs.ticketNo) {
          applyEditResponse(state.edit, meta.arg.originalArgs, payload, Date.now());
        }
      });
  },
});

export const {
  setBetType,
  setExpressSum,
  setBasketItemSum,
  setOneClickBet,
  resetBasketStatus,
  clearBasketRefusal,
  setBasketBonusCard,
  changeBasketItemValue,
  lockBasketItem,
  lockBasketLiveItems,
  startBetEdit,
  toggleBetEditOdd,
  removeBetEditOdd,
  resetBetEditMessage,
  cancelBetEdit,
} = basketSlice.actions;

export default basketSlice.reducer;
